import {
  fetchClientzoneSession,
  type ClientzoneSessionResponse,
} from "utils/clientzoneAuthApi";

const SESSION_KEY = "clientzone_session";
const TOKEN_KEY = "clientzone_token";
const EXPIRES_KEY = "clientzone_token_expires_at";

export type ClientSession = {
  customerId: number;
  loggedInAt: string;
  expiresAt: number;
};

export function getClientAccessToken(): string | null {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? String(token) : null;
}

export function getClientSession(): ClientSession | null {
  try {
    const token = getClientAccessToken();
    const expiresAt = Number(localStorage.getItem(EXPIRES_KEY) || 0);
    if (!token || !Number.isFinite(expiresAt) || expiresAt <= 0) {
      clearClientSession();
      return null;
    }

    if (Date.now() >= expiresAt * 1000) {
      clearClientSession();
      return null;
    }

    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) {
      clearClientSession();
      return null;
    }

    const parsed = JSON.parse(raw) as ClientSession;
    const customerId = Number(parsed?.customerId);
    if (!Number.isFinite(customerId) || customerId <= 0) {
      clearClientSession();
      return null;
    }

    return {
      customerId,
      loggedInAt: String(parsed.loggedInAt || ""),
      expiresAt,
    };
  } catch {
    clearClientSession();
    return null;
  }
}

export function isClientLoggedIn(): boolean {
  return Boolean(getClientSession());
}

export function setClientSessionFromAuthResponse(response: ClientzoneSessionResponse): void {
  const customerId = Number(response.customer_id);
  const expiresAt = Number(response.expires_at);
  const accessToken = String(response.access_token || "").trim();

  if (!accessToken || customerId < 1 || !Number.isFinite(expiresAt) || expiresAt <= 0) {
    throw new Error("Invalid login response from server.");
  }

  const session: ClientSession = {
    customerId,
    loggedInAt: new Date().toISOString(),
    expiresAt,
  };

  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(EXPIRES_KEY, String(expiresAt));
}

export async function validateClientSessionWithServer(): Promise<ClientSession | null> {
  const token = getClientAccessToken();
  const session = getClientSession();
  if (!token || !session) {
    clearClientSession();
    return null;
  }

  try {
    const status = await fetchClientzoneSession(token);
    if (Number(status.customer_id) !== session.customerId) {
      clearClientSession();
      return null;
    }
    localStorage.setItem(EXPIRES_KEY, String(status.expires_at));
    return {
      ...session,
      expiresAt: Number(status.expires_at),
    };
  } catch {
    clearClientSession();
    return null;
  }
}

export function clearClientSession(): void {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXPIRES_KEY);
}
